import { Button } from "@/components/ui/button";
import { ConditionRow } from "./ConditionRow";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface LogicalConditionRowProps {
    condition: Condition;
    onChange: (c?: Condition) => void;
    onDelete?: () => void;
    questions: Record<string, PolicyFormQuestion>;
}

export const LogicalConditionRow: React.FC<LogicalConditionRowProps> = ({ condition, onChange, onDelete, questions }) => {

    const handleChildChange = (index: number, child?: Condition) => {
        if ('conditions' in condition && Array.isArray(condition.conditions)) {
            const updated = [...condition.conditions];
            if (child) updated[index] = child;
            else updated.splice(index, 1);

            onChange({ ...condition, conditions: updated } as LogicalCondition);
        } else if (child) {
            const newLogical: LogicalCondition = {
                operator: "or",
                conditions: [condition as FieldCondition, child],
            };
            onChange(newLogical);
        }
    };

    const addFieldCondition = () => {
        if ('conditions' in condition && Array.isArray(condition.conditions)) {
            onChange({
                ...condition,
                conditions: [
                    ...condition.conditions,
                    { field: "", operator: "equals", value: "" } as FieldCondition,
                ],
            });
        } else if (typeof condition === "object" && condition !== null) {
            const fieldCond: FieldCondition = condition as FieldCondition;
            onChange({
                operator: "or",
                conditions: [
                    fieldCond,
                    { field: "", operator: "equals", value: "" } as FieldCondition,
                ],
            } as LogicalCondition);
        }
    };

    const addLogicalGroup = () => {
        if ('conditions' in condition && Array.isArray(condition.conditions)) {
            onChange({
                ...condition,
                conditions: [
                    ...condition.conditions,
                    { operator: "and", conditions: [] } as LogicalCondition,
                ],
            });
        } else if (typeof condition === "object" && condition !== null) {
            const fieldCond: FieldCondition = condition as FieldCondition;
            onChange({
                operator: "or",
                conditions: [
                    fieldCond,
                    { operator: "and", conditions: [] } as LogicalCondition,
                ],
            } as LogicalCondition);
        }
    };


    function isLogicalCondition(c: Condition): c is LogicalCondition {
        return 'operator' in c && 'conditions' in c;
    }

    return (
        <div className="pl-4 border-l border-gray-300 space-y-2">
            {isLogicalCondition(condition) && (
                <div className="flex items-center gap-2 justify-between">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Logic:</span>
                        <Select
                            value={condition.operator}
                            onValueChange={(op) =>
                                onChange({ ...condition, operator: op as 'and' | 'or' })
                            }
                        >
                            <SelectTrigger className="w-24">
                                <SelectValue placeholder="Select operator" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="and">AND</SelectItem>
                                <SelectItem value="or">OR</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {onDelete && (
                        <Button size="sm" variant="destructive" onClick={onDelete}>
                            ✕
                        </Button>
                    )}
                </div>
            )}

            {isLogicalCondition(condition) ? (
                condition.conditions.map((c, i) =>
                    'operator' in c && 'conditions' in c ? (
                        <LogicalConditionRow
                            key={`logical-${i}-${JSON.stringify(c).substring(0, 20)}`}
                            condition={c as LogicalCondition}
                            onChange={(updated) => handleChildChange(i, updated)}
                            onDelete={() => handleChildChange(i)}
                            questions={questions}
                        />
                    ) : (
                        <ConditionRow
                            key={`field-${i}-${(c as FieldCondition).field || 'empty'}`}
                            condition={c as FieldCondition}
                            onChange={(updated) => handleChildChange(i, updated)}
                            onDelete={() => handleChildChange(i)}
                            questions={questions}

                        />
                    )
                )
            ) : (
                <ConditionRow
                    condition={condition as FieldCondition}
                    onChange={(a) => onChange(a)}
                    onDelete={onDelete}
                    questions={questions}
                />
            )}

            <div className="flex gap-2 mt-1">
                <Button size="sm" variant="outline" onClick={addFieldCondition}>
                    + Add condition
                </Button>
                <Button size="sm" variant="outline" onClick={addLogicalGroup}>
                    + Add AND/OR group
                </Button>
            </div>
        </div>
    );
};